import { Component } from '@angular/core';
import { CheckboxWidget as _CheckboxWidget } from '../angular2-schema-form-shim';
import { JSONSchema, SchemaArray } from '../types/json-schema';
import { isArray } from '../forms/schemas';
import { isWidgetRequired } from './overrides';

export type CheckboxOptionValue = string | number;

export type CheckboxOption = {
  value: CheckboxOptionValue;
  label: string;
};

@Component( {
  selector: 'sp-checkbox-widget',
  templateUrl: './checkbox.widget.html',
} )
export class CheckboxWidget extends _CheckboxWidget {
  schema: JSONSchema;
  checked: { [ key: string ]: boolean };
  options: Array<CheckboxOption> = [];

  get required() {
    return isWidgetRequired( this );
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    this.setupOptions();
    this.loadValues();

    if ( this.isArray ) {
      this.formProperty.valueChanges.subscribe( ( value: string[] | null ) => {
        const vals = value || [];
        this.checked = vals.reduce( ( acc: { [ key: string ]: boolean }, v: string ) => {
          acc[ v ] = true;
          return acc;
        }, {} );
      } );
    }
  }

  setupOptions() {
    if ( this.schema.type !== 'array' ) return;
    let items = this.schema.items;
    if ( isArray( items ) ) {
      throw new TypeError( 'Cannot use array of schemas for checkbox items.' );
    }
    let enumTitles = items.options.enum_titles;
    this.options = items.enum.map( ( value, index ) => ( {
      value: value,
      label: enumTitles[ index ],
    } ) );
  }

  get isArray() {
    return this.schema.type === 'array';
  }

  loadValues() {
    if ( this.isArray ) {
      let value: string[] = this.formProperty.value || this.schema.default || null;
      this.checked = value ? value.reduce( ( checked, value ) => { checked[ value ] = true; return checked; }, {} ) : {};
      if ( !this.formProperty.value && this.schema.default ) {
        this.formProperty.setValue( this.schema.default, false );
      }
    }
  }

  checkToggle( option: CheckboxOption ) {
    const clickedValue = option.value.toString();

    if ( this.checked[ clickedValue ] ) {
      // Remove this option
      delete this.checked[ clickedValue ];
    } else {
      // Remove all exclusive options
      this.exclusiveOptions.forEach( option => delete this.checked[ option.value ] );
      // Remove all options if this one is exclusive
      if ( this.exclusiveOptions.map( option => option.value ).includes( clickedValue ) ) {
        this.checked = {};
      }
      // Add this option
      this.checked[ clickedValue ] = true;
    }

    this.formProperty.setValue( Object.keys( this.checked ), false );
  }

  get exclusiveOptions(): Array<CheckboxOption> {
    return this.exclusiveOptionValues
      .map( ( exclusiveValue: CheckboxOptionValue ) => {
        return this.options.find( option => option.value === exclusiveValue );
      } )
      .filter( option => typeof option !== 'undefined' );
  }

  get exclusiveOptionValues(): Array<CheckboxOptionValue> {
    let items = this.schema.items;
    if ( isArray( items ) ) return [];
    return ( items.options && items.options.enumExclusive && items.options.enumExclusive ) || [];
  }

  isExclusive( option: CheckboxOption ): Boolean {
    return this.exclusiveOptionValues.includes( option.value );
  }

  isChecked( value: CheckboxOptionValue ): boolean {
    return this.checked[ value.toString() ];
  }
}



// WEBPACK FOOTER //
// /var/www/schemas-forms/src/app/widgets/checkbox.widget.ts